export const calcParagraphMargin = (lineHeight, paragraphSpacing, fontSize) =>
  lineHeight * (paragraphSpacing / 100) * fontSize;

// Measure the rendered height of textbox html using an off-screen div that
// mirrors the real textbox's typographic CSS. Shared between the Textbox
// component (for post-edit height reconciliation) and bulk ops that mutate
// value without going through edit mode (e.g. merge).
export const computeWrappedTextHeight = (html, elementData) => {
  const {
    uniqueId,
    lineHeight,
    letterSpacing,
    width,
    textAlign,
    color,
    fontSize,
    fontFamily,
    bold,
    textDecoration,
    textCase,
    paragraphSpacing
  } = elementData;

  const stripped = stripParagraphSpacers(html);

  const div = document.createElement("div");
  div.style.cssText = [
    "position:absolute",
    "visibility:hidden",
    `line-height:${lineHeight}em`,
    `letter-spacing:${letterSpacing / 1000}em`,
    `width:${Number(width)}px`,
    `text-align:${textAlign}`,
    `color:${color}`,
    `font-size:${Number(fontSize).toFixed(2)}px`,
    `font-family:"${fontFamily}"`,
    `font-weight:${bold}`,
    `text-decoration:${textDecoration || "none"}`,
    "word-break:break-word",
    `text-transform:${textCase === "sentence" || !textCase ? "none" : textCase}`
  ].join(";");
  div.style.setProperty("--font-size", fontSize);
  div.style.setProperty("--paragraph-spacing", paragraphSpacing);
  div.style.setProperty("--line-height", lineHeight);
  div.innerHTML = stripped;
  document.body.appendChild(div);

  createStyleElement(`simulated-wrap-${uniqueId}`);
  applyParagraphStyling(div);

  const height = div.clientHeight;
  document.body.removeChild(div);
  return height;
};

export const createStyleElement = elementId => {
  const styleId = `sheet-${elementId}`;
  if (!!document.querySelector(`style[id="${styleId}"]`)) return;
  // Create a new style element
  const style = document.createElement("style");

  style.setAttribute("id", styleId);

  document.head.appendChild(style);
};

export const applyParagraphStyling = textboxElement => {
  if (!textboxElement) return;

  // Split inline elements that straddle paragraph breaks so <br><br>
  // is visible at the block level for wrapping.
  splitInlinesAtParagraphBreaks(textboxElement);

  // Find the content container. When called with the outer textbox ref,
  // the actual content (text nodes, <br> elements) lives inside a child div.
  // Recurse into element children to find and style all content containers.
  const container = findContentContainer(textboxElement);
  if (!container) return;

  const computedStyle = window.getComputedStyle(textboxElement);
  const parsedFontSize = parseFloat(
    computedStyle.getPropertyValue("--font-size") || computedStyle.fontSize
  );
  const fontSize = Number.isNaN(parsedFontSize) ? 16 : parsedFontSize;
  const parsedParagraphSpacing = parseFloat(
    computedStyle.getPropertyValue("--paragraph-spacing")
  );
  const paragraphSpacing = Number.isNaN(parsedParagraphSpacing)
    ? 100
    : parsedParagraphSpacing;
  const parsedLineHeight = parseFloat(
    computedStyle.getPropertyValue("--line-height")
  );
  const lineHeight = Number.isNaN(parsedLineHeight) ? 1.4 : parsedLineHeight;

  const marginStr = `${calcParagraphMargin(
    lineHeight,
    paragraphSpacing,
    fontSize
  ).toFixed(2)}px`;

  applyWrapping(container, marginStr);
};

// Inline elements that must not become wrapping containers (block divs
// inside an inline element is invalid HTML and the parser will auto-close
// the inline element, silently dropping its styling).
const INLINE_TAGS = new Set([
  "SPAN",
  "B",
  "I",
  "U",
  "A",
  "STRONG",
  "EM",
  "S",
  "FONT",
  "MARK",
  "SUB",
  "SUP"
]);

/**
 * Splits inline elements (span, b, etc.) that contain <br><br> paragraph
 * breaks into separate copies around the breaks. For example:
 *   <span style="font-weight:bold">one<br><br>two</span>
 * becomes:
 *   <span style="font-weight:bold">one</span><br><br><span style="font-weight:bold">two</span>
 *
 * This allows the parent block element to see <br><br> at the top level,
 * enabling paragraph wrapping without producing invalid HTML (block divs
 * inside inline elements).
 */
const splitInlinesAtParagraphBreaks = element => {
  const children = Array.from(element.childNodes);
  for (const child of children) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    // Recurse into children first so nested inlines are split bottom-up
    splitInlinesAtParagraphBreaks(child);

    if (!INLINE_TAGS.has(child.nodeName)) continue;

    const childNodes = Array.from(child.childNodes);
    const hasParagraphBreak = childNodes.some(
      (n, i) => n.nodeName === "BR" && childNodes[i + 1]?.nodeName === "BR"
    );
    if (!hasParagraphBreak) continue;

    // Split the inline element at each <br><br> boundary
    const fragments = [];
    let currentClone = child.cloneNode(false);

    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      const next = childNodes[i + 1];

      if (node.nodeName === "BR" && next?.nodeName === "BR") {
        if (currentClone.childNodes.length > 0) {
          fragments.push(currentClone);
        }
        fragments.push(document.createElement("br"));
        fragments.push(document.createElement("br"));
        i++; // skip second BR
        currentClone = child.cloneNode(false);
      } else {
        currentClone.appendChild(node.cloneNode(true));
      }
    }
    if (currentClone.childNodes.length > 0) {
      fragments.push(currentClone);
    }

    // Replace the original inline element with the split fragments
    const parent = child.parentNode;
    for (const frag of fragments) {
      parent.insertBefore(frag, child);
    }
    parent.removeChild(child);
  }
};

const findContentContainer = element => {
  // Check if this element directly contains BR elements or paragraph wrappers
  const children = Array.from(element.childNodes);
  const hasBr = children.some(n => n.nodeName === "BR");
  const hasWrappers = element.querySelector(":scope > .paragraph");

  // Only return this element as a container if it is block-level.
  // Inline elements (span, b, i, a, etc.) must not contain paragraph
  // wrapper divs — the HTML5 parser would auto-close the inline element.
  if ((hasBr || hasWrappers) && !INLINE_TAGS.has(element.nodeName)) {
    return element;
  }

  // Content is nested — recurse into element children
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const found = findContentContainer(child);
      if (found) return found;
    }
  }
  return null;
};

const applyWrapping = (container, marginStr) => {
  // Check if paragraph wrappers already exist — update margins in-place
  const existingWrappers = container.querySelectorAll(":scope > .paragraph");
  if (existingWrappers.length > 0) {
    const wrapperArray = Array.from(existingWrappers);
    wrapperArray.forEach((wrapper, i) => {
      const isLast = i === wrapperArray.length - 1;
      const expectedMargin = isLast ? "" : marginStr;
      if (wrapper.style.marginBottom !== expectedMargin) {
        wrapper.style.marginBottom = expectedMargin;
      }
    });
    return; // Wrappers exist — margins updated in-place, no restructuring needed
  }

  // Collect all child nodes and group into paragraphs split at <br><br> boundaries
  const childNodes = Array.from(container.childNodes);
  if (childNodes.length === 0) return;

  const paragraphs = [];
  let currentGroup = [];

  for (let i = 0; i < childNodes.length; i++) {
    const node = childNodes[i];
    const next = childNodes[i + 1];

    if (node.nodeName === "BR" && next && next.nodeName === "BR") {
      // Double <br> = paragraph break. First BR goes into current group, second is consumed.
      currentGroup.push(node);
      paragraphs.push(currentGroup);
      currentGroup = [];
      i++; // skip the second BR
    } else {
      currentGroup.push(node);
    }
  }
  if (currentGroup.length > 0) {
    paragraphs.push(currentGroup);
  }

  // If only one paragraph (no double-breaks), no wrapping needed
  if (paragraphs.length <= 1) return;

  // Build wrappers using cloneNode so original nodes stay in the container
  // until we're committed to replacing them. This avoids content loss when
  // filtering leaves fewer than 2 viable wrappers (e.g. leading <br><br>x).
  const wrappers = [];
  paragraphs.forEach(group => {
    const wrapper = document.createElement("div");
    wrapper.className = "paragraph";
    group.forEach(node => wrapper.appendChild(node.cloneNode(true)));

    // Remove trailing <br> from the group (the first BR of the pair that was kept)
    const lastChild = wrapper.lastChild;
    if (lastChild && lastChild.nodeName === "BR") {
      wrapper.removeChild(lastChild);
    }

    // Skip empty groups (produced by consecutive paragraph breaks)
    if (wrapper.childNodes.length === 0) return;

    wrappers.push(wrapper);
  });

  // If collapsing left only one non-empty wrapper, no wrapping needed
  if (wrappers.length <= 1) return;

  // Clear container and append viable wrappers
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  wrappers.forEach((wrapper, i) => {
    if (i < wrappers.length - 1) {
      wrapper.style.marginBottom = marginStr;
    }
    container.appendChild(wrapper);
  });
};

/**
 * Wraps HTML string with paragraph divs at <br><br> boundaries,
 * applying margin-bottom for paragraph spacing. Uses DOM parsing to
 * correctly handle spans that straddle paragraph boundaries.
 */
export const wrapParagraphSpacersHTML = (
  html,
  fontSize,
  lineHeight,
  paragraphSpacing
) => {
  if (!html) return html;
  // Already wrapped
  if (html.includes('class="paragraph"')) return html;

  // Use a detached DOM element so inline spans crossing paragraph
  // boundaries are handled correctly (string-splitting would cleave them).
  const tmp = document.createElement("div");
  tmp.innerHTML = html;

  splitInlinesAtParagraphBreaks(tmp);

  const container = findContentContainer(tmp);
  if (!container) return html;

  const marginStr = `${calcParagraphMargin(
    lineHeight,
    paragraphSpacing,
    fontSize
  ).toFixed(2)}px`;
  applyWrapping(container, marginStr);

  // If no wrapping was applied (single paragraph), return original html
  if (!tmp.querySelector(".paragraph")) return html;

  return tmp.innerHTML;
};

export const stripParagraphSpacers = html => {
  if (!html) return html;
  if (!html.includes("paragraph")) return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const paragraphs = tmp.querySelectorAll(".paragraph");
  if (paragraphs.length === 0) return html;
  const parts = Array.from(paragraphs).map(p => p.innerHTML);
  return parts.join("<br><br>");
};
